import { Component, EventEmitter, Input, Output, SimpleChanges, ViewChildren } from '@angular/core';
import { AlarmSettingFormComponent } from '@components/alarm-setting-form/alarm-setting-form.component';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '@service/auth.service';
import { ChannelFormatterService } from '@service/channel-formatter.service';
import { ChannelService } from '@service/channel.service';
import { ConfirmationPromptService } from '@service/confirmation-prompt.service';
import { DeviceFlagsService } from '@service/device-flags.service';
import { ElementSettingsService } from '@service/element-settings.service';
import { MimicService } from '@service/mimic.service';
import { NavigationService } from '@service/navigation.service';
import { NotificationService } from '@service/notification.service';
import { TrendGraphViewsService } from '@service/trend-graph-views.service';
import { AlarmSetting } from 'ops/common/models/alarm-setting';
import { Channel, ChannelFns, FlagOption } from 'ops/common/models/channel';
import { ElementSettings } from 'ops/common/models/editor-viewer-element';
import { Mimic } from 'ops/common/models/mimic';
import { TrendGraphView } from 'ops/common/models/trend-graph-view';
import {
    AlarmSettingValidationField,
    alarmSettingValidator,
    ObjectSchema,
} from 'ops/common/validators';
import { lastValueFrom } from 'rxjs';

@Component({
    selector: 'app-channel-settings-dropdown',
    templateUrl: './channel-settings-dropdown.component.html',
    styleUrls: [
        '../channel-settings-form/channel-settings-form.component.scss',
        './channel-settings-dropdown.component.scss',
    ],
    standalone: false,
})
export class ChannelSettingsDropdownComponent {
    readonly ADDRESS_STRING = 'FCU';

    mimics: Mimic[] = [];
    trendGraphs: TrendGraphView[] = [];
    elementSettings: ElementSettings[];
    vesselMimics: Mimic[];
    validators: { [key: string]: ObjectSchema<AlarmSetting> } = {
        veryLow: null,
        low: null,
        high: null,
        veryHigh: null,
        feedback: null,
        sensorFail: null,
        alarmSetting: null,
    };

    userPermission: boolean = false;

    currentFlag: FlagOption = FlagOption.UNDEFINED;

    @ViewChildren('alarmSettingForm') alarmSettingForm: AlarmSettingFormComponent[];

    @Input() value: number = 0;
    @Input() channel!: Channel;
    @Input() isSelectedChannel: boolean = false;
    @Output() onSubmit: EventEmitter<Channel> = new EventEmitter();
    @Output() onCloseClicked = new EventEmitter();
    @Output() onAdvancedSettings = new EventEmitter<Channel>();
    @Output() onDeleteAlarm = new EventEmitter<Channel>();

    private shouldLoadGraphs: boolean = false;
    private shouldLoadMimics: boolean = false;

    scaleDecimalPlaces = 0;

    constructor(
        private translate: TranslateService,
        private confirmationPromptService: ConfirmationPromptService,
        private channelService: ChannelService,
        private graphService: TrendGraphViewsService,
        private navigationService: NavigationService,
        private mimicService: MimicService,
        private elementSettingsService: ElementSettingsService,
        private notificationService: NotificationService,
        private authService: AuthService,
        private channelFormatterService: ChannelFormatterService,
        private flagsService: DeviceFlagsService,
    ) {}

    ngOnInit(): void {
        this.authService.currentUser$.subscribe(async (user) => {
            this.userPermission = await this.authService.checkChannelSettingPermission(
                user.userId,
                this.channel.groupId,
            );
        });
    }

    async ngOnChanges(changes: SimpleChanges) {
        if (changes.channel && this.channel) {
            this.elementSettings = await this.elementSettingsService.getAll();
            this.vesselMimics = await this.mimicService.getAll();
            this.setMimics();

            let newValidators = {} as { [key: string]: ObjectSchema<AlarmSetting> };

            newValidators.veryLow = alarmSettingValidator(
                this.channel,
                AlarmSettingValidationField.ALARM_VERY_LOW,
            );
            newValidators.low = alarmSettingValidator(
                this.channel,
                AlarmSettingValidationField.ALARM_LOW,
            );
            newValidators.high = alarmSettingValidator(
                this.channel,
                AlarmSettingValidationField.ALARM_HIGH,
            );
            newValidators.veryHigh = alarmSettingValidator(
                this.channel,
                AlarmSettingValidationField.ALARM_VERY_HIGH,
            );
            newValidators.feedback = alarmSettingValidator(
                this.channel,
                AlarmSettingValidationField.ALARM_SETTING_FEEDBACK_ERROR,
            );
            newValidators.sensorFail = alarmSettingValidator(
                this.channel,
                AlarmSettingValidationField.ALARM_SETTING_SENSOR_FAIL,
            );
            newValidators.alarmSetting = alarmSettingValidator(
                this.channel,
                AlarmSettingValidationField.ALARM_SETTING,
            );

            this.validators = newValidators;
            this.scaleDecimalPlaces = Math.log10(this.channel.fcuData.scale);

            const userId = this.authService.getCurrentUserId();
            this.userPermission = await this.authService.checkChannelSettingPermission(
                userId,
                this.channel.groupId,
            );

            if (this.isSelectedChannel) {
                if (!this.shouldLoadGraphs) {
                    this.graphService.getAll().subscribe((graphs) => {
                        this.setGraphs(graphs);
                    });
                    this.shouldLoadGraphs = true;
                }
                if (!this.shouldLoadMimics) {
                    this.shouldLoadMimics = true;
                    this.elementSettings = await this.elementSettingsService.getAll();
                    this.vesselMimics = await this.mimicService.getAll();
                    this.setMimics();
                }
            } else {
                if (this.shouldLoadGraphs) {
                    this.shouldLoadGraphs = false;
                }
                if (this.shouldLoadMimics) {
                    this.shouldLoadMimics = false;
                }
            }
        }
    }

    transform(value: number) {
        const transformed = ChannelFns.transformValue(this.channel, value);
        return typeof transformed === 'number'
            ? transformed.toFixed(this.scaleDecimalPlaces)
            : transformed;
    }

    cloneChannel(): Channel {
        return JSON.parse(JSON.stringify(this.channel));
    }

    getScale() {
        return this.channel.fcuData.scale;
    }
    isDigital() {
        return ChannelFns.isDigital(this.channel);
    }
    isAnalog() {
        return ChannelFns.isAnalog(this.channel);
    }
    isRunningHour() {
        return ChannelFns.isRunningHour(this.channel);
    }

    getTag() {
        return this.channel.opsData.tag;
    }
    getName() {
        return this.channel.opsData.name;
    }
    getValue() {
        return this.transform(this.value);
    }
    getRawValue() {
        return this.value;
    }
    getUnits() {
        return this.channel.opsData.measurementUnit;
    }
    getMeasurableMin() {
        const scale = this.channel.fcuData.scale;
        const decimalPlaces = scale.toString().length - 1;
        const value = this.channel.fcuData.measurableRange.min / Math.pow(10, decimalPlaces);
        return value.toFixed(decimalPlaces);
    }
    getMeasurableMax() {
        const scale = this.channel.fcuData.scale;
        const decimalPlaces = scale.toString().length - 1;
        const value = this.channel.fcuData.measurableRange.max / Math.pow(10, decimalPlaces);
        return value.toFixed(decimalPlaces);
    }

    async onGlobalReposeClicked(isChecked: boolean) {
        const reposeState = isChecked;

        const oldChannel = this.cloneChannel();

        if (this.channel.fcuData.digitalAlarmSetting.enable)
            this.channel.fcuData.digitalAlarmSetting.manualReposeFlag = reposeState;
        if (this.channel.fcuData.alarmSettingFeedbackError.enable)
            this.channel.fcuData.alarmSettingFeedbackError.manualReposeFlag = reposeState;
        if (this.channel.fcuData.alarmSettingVeryHigh.enable)
            this.channel.fcuData.alarmSettingVeryHigh.manualReposeFlag = reposeState;
        if (this.channel.fcuData.alarmSettingHigh.enable)
            this.channel.fcuData.alarmSettingHigh.manualReposeFlag = reposeState;
        if (this.channel.fcuData.alarmSettingLow.enable)
            this.channel.fcuData.alarmSettingLow.manualReposeFlag = reposeState;
        if (this.channel.fcuData.alarmSettingVeryLow.enable)
            this.channel.fcuData.alarmSettingVeryLow.manualReposeFlag = reposeState;

        try {
            await lastValueFrom(this.channelService.update(this.channel));
            this.notificationService.channelUpdateComplete();
        } catch (err) {
            this.channel = oldChannel;
        }
    }

    emitSubmit(alarmSettingKey: string, alarmSetting: AlarmSetting) {
        this.createAlarmCallback(alarmSettingKey, this.onSubmit)(alarmSetting);
    }

    emitDelete(alarmSettingKey: string, alarmSetting: AlarmSetting) {
        this.confirmationPromptService.onOptionSelected().subscribe((confirm) => {
            if (confirm) {
                this.createAlarmCallback(alarmSettingKey, this.onDeleteAlarm)(alarmSetting);
            }
        });

        this.confirmationPromptService.showPrompt({
            mainLabel: this.translate.instant('forms.channel.alarm.delete-confirmation-prompt'),
            confirmLabel: this.translate.instant('forms.channel.alarm.delete-confirmation-button'),
            cancelLabel: this.translate.instant('forms.channel.alarm.delete-cancel-button'),
        });
    }

    createAlarmCallback(alarmSettingKey: string, event: EventEmitter<Channel>) {
        return (alarmSetting: AlarmSetting) => {
            const newChannel = this.cloneChannel();
            newChannel.fcuData[alarmSettingKey] = alarmSetting;
            event.emit(newChannel);
        };
    }

    getGlobalReposeValue(): boolean {
        let manualReposeFlag: boolean = false;

        if (this.channel.fcuData.digitalAlarmSetting.enable)
            manualReposeFlag = this.channel.fcuData.digitalAlarmSetting.manualReposeFlag;
        if (this.channel.fcuData.alarmSettingFeedbackError.enable)
            manualReposeFlag = this.channel.fcuData.alarmSettingFeedbackError.manualReposeFlag;
        if (this.channel.fcuData.alarmSettingVeryHigh.enable)
            manualReposeFlag = this.channel.fcuData.alarmSettingVeryHigh.manualReposeFlag;
        if (this.channel.fcuData.alarmSettingHigh.enable)
            manualReposeFlag = this.channel.fcuData.alarmSettingHigh.manualReposeFlag;
        if (this.channel.fcuData.alarmSettingLow.enable)
            manualReposeFlag = this.channel.fcuData.alarmSettingLow.manualReposeFlag;
        if (this.channel.fcuData.alarmSettingVeryLow.enable)
            manualReposeFlag = this.channel.fcuData.alarmSettingVeryLow.manualReposeFlag;

        return manualReposeFlag;
    }

    getChannelType() {
        return this.channelFormatterService.getChannelType(this.channel);
    }

    getFcuNum() {
        return this.channelFormatterService.getFcuNum(this.channel);
    }

    getInputAddress() {
        return this.channelFormatterService.getInputAddress(this.channel);
    }

    getOutputSignalType() {
        return this.channelFormatterService.getOutputSignalType(this.channel);
    }

    getOutputAddress() {
        return this.channelFormatterService.getOutputAddress(this.channel);
    }

    setMimics() {
        const mimicIdsSet = new Set();
        this.mimics = [];
        for (let i = 0; i < this.elementSettings?.length; i++) {
            if (this.isInMimic(this.channel, this.elementSettings[i])) {
                mimicIdsSet.add(this.elementSettings[i].mimicId);
            }
        }

        this.vesselMimics.forEach((mimic) => {
            if (mimicIdsSet.has(mimic.id)) {
                this.mimics.push(mimic);
            }
        });
    }

    isInMimic(channel: Channel, elementSetting: ElementSettings) {
        const channelTag = channel.fcuData.fcuChannelId.toString();
        return (
            channelTag === elementSetting?.flashingStatus?.channelTag ||
            channelTag === elementSetting?.disabledStatus?.channelTag ||
            channelTag === elementSetting?.channelTag ||
            channelTag === elementSetting?.valveSetting?.openChannel?.tag ||
            channelTag === elementSetting?.valveSetting?.closeChannel?.tag ||
            channelTag === elementSetting?.valveSetting?.flashChannel?.tag
        );
    }

    setGraphs(graphs: TrendGraphView[]) {
        this.trendGraphs = [];
        for (let i = 0; i < graphs.length; i++) {
            if (this.isInGraph(this.channel, graphs[i])) {
                this.trendGraphs.push(graphs[i]);
            }
        }
    }

    isInGraph(channel: Channel, graph: TrendGraphView) {
        return (
            graph.channels.find((v) => v.fcuChannelId == channel.fcuData.fcuChannelId) !== undefined
        );
    }

    onClose() {
        this.onCloseClicked.emit();
    }

    emitAdvancedDetails() {
        this.onAdvancedSettings.emit(this.channel);
    }

    toTrendGraphView(id: string) {
        this.navigationService.toTrendGraphView(id);
    }

    toMimicView(id: string) {
        this.navigationService.toMimic(id);
    }

    closePreviewForm() {
        this.alarmSettingForm.forEach((alarmSettingForm) => alarmSettingForm.closeComponent());
    }

    getStatus() {
        this.flagsService.getStatus(this.channel);
    }
}
